import logger from '../utils/logger';
import api from '../config/api';
import stripePaymentService from './stripePaymentService';

const billingService = {
  async getAllPlans() {
    try {
      const response = await api.get('/billing/plans');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching all plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch all plans'
      };
    }
  },

  async getPlans() {
    try {
      const response = await api.get('/billing/plans');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch plans'
      };
    }
  },

  async getPopularPlans() {
    try {
      const response = await api.get('/billing/plans/popular');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching popular plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch popular plans'
      };
    }
  },

  async getRecommendedPlans() {
    try {
      const response = await api.get('/billing/plans/recommended');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching recommended plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch recommended plans'
      };
    }
  },

  async getPlansByBillingInterval(interval) {
    try {
      const response = await api.get(`/billing/plans/billing-interval/${interval}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching plans by billing interval:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch plans by billing interval'
      };
    }
  },

  async getPlansByCurrency(currency) {
    try {
      const response = await api.get(`/billing/plans/currency/${currency}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching plans by currency:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch plans by currency'
      };
    }
  },

  getUserActivePlan: async () => {
    try {
      const response = await api.get('/user-subscriptions/active');
      return { success: true, data: response.data.data };
    } catch (error) {
  logger.error('Error fetching user active plan:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch user active plan' };
    }
  },

  // Admin Plan Management Methods
  async createPlan(planData) {
    try {
      const response = await api.post('/billing/plans', planData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error creating plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create plan'
      };
    }
  },

  async updatePlan(planId, planData) {
    try {
      const response = await api.put(`/billing/plans/${planId}`, planData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error updating plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update plan'
      };
    }
  },

  async deletePlan(planId) {
    try {
      await api.delete(`/billing/plans/${planId}`);
      return {
        success: true
      };
    } catch (error) {
  logger.error('Error deleting plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete plan'
      };
    }
  },

  async togglePlanActive(planId) {
    try {
      const response = await api.patch(`/billing/plans/${planId}/toggle-active`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error toggling plan active status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to toggle plan active status'
      };
    }
  },

  async getPlanByCode(code) {
    try {
      const response = await api.get(`/billing/plans/${code}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch plan'
      };
    }
  },

  async initiatePayment(planData, userData) {
    try {
      // Map database plan fields to Stripe payment service expected format
      const mappedPlanData = {
        code: planData.code,
        name: planData.name,
        amount: planData.priceAmount / 100, // Convert from cents to USD
        currency: planData.currency,
        billingInterval: planData.billingInterval
      };

      const result = await stripePaymentService.createPaymentLink(mappedPlanData, userData);
      
      if (result.success) {
        if (!result.data || !result.data.paymentUrl) {
          return {
            success: false,
            error: 'Payment URL is missing from response'
          };
        }
        
        return {
          success: true,
          data: {
            paymentUrl: result.data.paymentUrl,
            paymentId: result.data.paymentId,
            sessionId: result.data.sessionId
          }
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Failed to initiate payment'
      };
    }
  },

  async checkPaymentStatus(sessionId) {
    try {
      const result = await stripePaymentService.getPaymentStatus(sessionId);
      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
  logger.error('Error checking Stripe payment status:', error);
      return {
        success: false,
        error: 'Failed to check payment status'
      };
    }
  },

  async checkDirectPaymentStatus(sessionId) {
    try {
  logger.debug('Checking direct Stripe payment status for:', sessionId);
      const result = await stripePaymentService.getDirectPaymentStatus(sessionId);
  logger.debug('Direct Stripe payment status result:', result);
      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
  logger.error('Error checking direct Stripe payment status:', error);
      return {
        success: false,
        error: 'Failed to check direct payment status'
      };
    }
  },

  async activateUserPlan(planCode, sessionId) {
    try {
      const result = await stripePaymentService.activatePlan(planCode, sessionId);
      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
  logger.error('Error activating Stripe user plan:', error);
      return {
        success: false,
        error: 'Failed to activate plan'
      };
    }
  },

  async getPaymentStatus(sessionId) {
    try {
      const response = await api.get(`/payments/stripe/status/${sessionId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
  logger.error('Error fetching Stripe payment status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch payment status'
      };
    }
  }
};

export default billingService;
